import { type ListServiceRolesQuery, type ServiceRole } from '@clockwise/clients/service-roles';
import { ApiError } from '@clockwise/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Download,
  Filter,
  Pencil,
  Plus,
  Trash2,
  Undo2,
  Upload,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { CatalogFormSheet } from '@/components/custom/catalog-form-sheet';
import {
  DEFAULT_KIND,
  KIND_FILTERS,
  KIND_LABELS,
  KindBadge,
  type KindFilter,
  kindFilterToQuery,
} from '@/components/custom/kind-filter';
import { RequirePermission } from '@/components/custom/require-permission';
import { RowActions } from '@/components/custom/row-actions';
import { SearchInput } from '@/components/custom/search-input';
import { ServiceRoleImportSheet } from '@/components/custom/service-role-import-sheet';
import { StickyTableScroll } from '@/components/custom/sticky-table-scroll';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { serviceRolesApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { queryKeys } from '@/lib/query-keys';

const SORT_FIELD_VALUES = ['name', 'shortCode', 'createdAt', 'updatedAt'] as const;
type SortField = (typeof SORT_FIELD_VALUES)[number];

const listSearch = z.object({
  q: z.string().optional(),
  status: z.enum(['active', 'inactive', 'all']).optional(),
  // `.catch(DEFAULT_KIND)` so a stale bookmark like `?kind=archived`
  // or a typo silently falls back to the default ("all") instead of
  // throwing into TanStack Router's error boundary. The default-
  // stripping in `stripDefaults` rewrites the URL on the next
  // navigate, so the bad value doesn't linger.
  kind: z.enum(KIND_FILTERS).catch(DEFAULT_KIND).optional(),
  page: z.coerce.number().int().min(0).optional(),
  sortField: z.enum(SORT_FIELD_VALUES).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
});
type ListSearch = z.infer<typeof listSearch>;

export const Route = createFileRoute('/_authenticated/setup/service-roles/')({
  validateSearch: listSearch,
  component: ServiceRolesPage,
});

const PAGE_SIZE = 25;
const DEFAULT_SORT_FIELD: SortField = 'name';

function ServiceRolesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const sortField: SortField = search.sortField ?? DEFAULT_SORT_FIELD;
  const sortDirection: 'asc' | 'desc' = search.sortDirection ?? 'asc';
  const page = search.page ?? 0;
  const q = search.q ?? '';
  const status = search.status ?? 'active';
  const kindFilter: KindFilter = search.kind ?? DEFAULT_KIND;

  const sorting: SortingState = [{ id: sortField, desc: sortDirection === 'desc' }];

  const updateSearch = (next: Partial<ListSearch>) =>
    navigate({
      search: (prev: ListSearch) => stripDefaults({ ...prev, ...next }),
      replace: true,
    });

  // One sheet serves both add and edit — `editing` null means create.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceRole | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const queryClient = useQueryClient();

  // Export mirrors the on-screen filters exactly — what the user sees
  // is what they get in the file. Includes the active selector, the
  // search box text, and the chosen sort order. Reading from the same
  // URL state the list query consumes keeps the two in lock-step.
  const exportMutation = useMutation({
    mutationFn: () =>
      serviceRolesApi.exportXlsx({
        active: status === 'all' ? undefined : status === 'active',
        internal: kindFilterToQuery(kindFilter),
        q: q.trim() || undefined,
        sortField,
        sortDirection,
      }),
    onSuccess: ({ blob, filename }) => {
      triggerDownload(blob, filename ?? 'service-roles.xlsx');
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Export failed';
      toast.error(msg);
    },
  });

  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => {
    setSearchInput(q);
  }, [q]);
  useEffect(() => {
    if (searchInput === q) return;
    const handle = setTimeout(() => updateSearch({ q: searchInput, page: 0 }), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, q]);

  const listQuery: ListServiceRolesQuery = {
    q: q.trim() || undefined,
    active: status === 'all' ? undefined : status === 'active',
    internal: kindFilterToQuery(kindFilter),
    sortField,
    sortDirection,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: queryKeys.serviceRoles.list(listQuery),
    queryFn: () => serviceRolesApi.list(listQuery),
    placeholderData: keepPreviousData,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => serviceRolesApi.remove(id),
    onSuccess: () => {
      toast.success('Role deactivated');
      queryClient.invalidateQueries({ queryKey: queryKeys.serviceRoles.all });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Delete failed'),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => serviceRolesApi.restore(id),
    onSuccess: () => {
      toast.success('Role reactivated');
      queryClient.invalidateQueries({ queryKey: queryKeys.serviceRoles.all });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Restore failed'),
  });

  const columns = useMemo<ColumnDef<ServiceRole>[]>(
    () => [
      {
        accessorKey: 'shortCode',
        header: 'Code',
        cell: ({ row }) => (
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] uppercase text-muted-foreground">
            {row.original.shortCode}
          </span>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to="/setup/service-roles/$id"
            params={{ id: row.original.id }}
            className="font-medium text-primary underline-offset-2 hover:underline"
            title={row.original.name}
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.name}
            {!row.original.active ? (
              <span className="ml-2 text-xs text-muted-foreground">(inactive)</span>
            ) : null}
          </Link>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.description ?? <span className="text-muted-foreground">—</span>,
      },
      {
        id: 'kind',
        accessorKey: 'internal',
        header: 'Kind',
        enableSorting: false,
        cell: ({ row }) => <KindBadge internal={row.original.internal} />,
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <RequirePermission permission="service_role.edit">
            <RowActions>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Edit"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(row.original);
                  setSheetOpen(true);
                }}
              >
                <Pencil />
              </Button>
              {row.original.active ? (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Deactivate"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Deactivate "${row.original.name}"?`)) {
                      deleteMutation.mutate(row.original.id);
                    }
                  }}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 />
                </Button>
              ) : (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Reactivate"
                  onClick={(e) => {
                    e.stopPropagation();
                    restoreMutation.mutate(row.original.id);
                  }}
                  disabled={restoreMutation.isPending}
                >
                  <Undo2 />
                </Button>
              )}
            </RowActions>
          </RequirePermission>
        ),
      },
    ],
    [deleteMutation, restoreMutation],
  );

  const table = useReactTable({
    data: query.data?.data ?? [],
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      if (!first) {
        updateSearch({ sortField: undefined, sortDirection: undefined });
        return;
      }
      updateSearch({
        sortField: first.id as SortField,
        sortDirection: first.desc ? 'desc' : 'asc',
        page: 0,
      });
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.max(1, Math.ceil((query.data?.meta.total ?? 0) / PAGE_SIZE)),
  });

  const total = query.data?.meta.total ?? 0;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, page * PAGE_SIZE + (query.data?.data.length ?? 0));

  return (
    <PageShell>
      <PageHeader
        title="Service roles"
        subtitle="Service-delivery roles (Bookkeeper, Accountant, CFO, …)."
        count={total}
      >
        <RequirePermission permission="service_role.view">
          <Button
            variant="outline"
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
          >
            <Download />
            {exportMutation.isPending ? 'Exporting…' : 'Export'}
          </Button>
        </RequirePermission>
        <RequirePermission permission="service_role.edit">
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload />
            Import
          </Button>
        </RequirePermission>
        <RequirePermission permission="service_role.edit">
          <Button
            onClick={() => {
              setEditing(null);
              setSheetOpen(true);
            }}
          >
            <Plus />
            Add role
          </Button>
        </RequirePermission>
      </PageHeader>

      <div
        data-surface="panel"
        className="flex flex-wrap items-center gap-3 rounded-md border bg-card p-6 py-4"
      >
        <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <Filter className="size-3.5" />
          Filters:
        </span>
        <SearchInput
          placeholder="Search name, code, description…"
          value={searchInput}
          onValueChange={setSearchInput}
        />
        <Select
          value={status}
          onValueChange={(v) => updateSearch({ status: v as ListSearch['status'], page: 0 })}
        >
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={kindFilter}
          onValueChange={(v) =>
            updateSearch({
              kind: (v as KindFilter) === DEFAULT_KIND ? undefined : (v as KindFilter),
              page: 0,
            })
          }
        >
          <SelectTrigger className="w-36" aria-label="Kind">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {KIND_FILTERS.map((f) => (
              <SelectItem key={f} value={f}>
                {KIND_LABELS[f]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <StickyTableScroll>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => {
                  const sortable = h.column.getCanSort();
                  const dir = h.column.getIsSorted();
                  return (
                    <TableHead key={h.id}>
                      {sortable ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 select-none hover:text-foreground"
                          onClick={h.column.getToggleSortingHandler()}
                        >
                          {flexRender(h.column.columnDef.header, h.getContext())}
                          {dir === 'asc' ? (
                            <ArrowUp className="size-3" />
                          ) : dir === 'desc' ? (
                            <ArrowDown className="size-3" />
                          ) : (
                            <ArrowUpDown className="size-3 opacity-40" />
                          )}
                        </button>
                      ) : (
                        flexRender(h.column.columnDef.header, h.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((_c, j) => (
                    <TableCell key={`skeleton-cell-${i}-${j}`}>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : query.isError ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-10 text-center text-destructive">
                  {query.error instanceof ApiError ? query.error.message : 'Failed to load roles.'}
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-10 text-center text-muted-foreground"
                >
                  {q ? 'No roles match this search.' : 'No service roles yet. Click “Add role”.'}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((r) => (
                <TableRow
                  key={r.id}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate({
                      to: '/setup/service-roles/$id',
                      params: { id: r.original.id },
                    })
                  }
                >
                  {r.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell ?? defaultCell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StickyTableScroll>

      <footer className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {total === 0 ? 'No results' : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: Math.max(0, page - 1) })}
            disabled={page === 0 || query.isFetching}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: page + 1 })}
            disabled={(page + 1) * PAGE_SIZE >= total || query.isFetching}
          >
            Next
          </Button>
        </div>
      </footer>

      <CatalogFormSheet
        open={sheetOpen}
        onOpenChange={(o) => {
          setSheetOpen(o);
          if (!o) setEditing(null);
        }}
        entityLabel="service role"
        entity={
          editing
            ? {
                id: editing.id,
                name: editing.name,
                code: editing.shortCode,
                description: editing.description,
                kind: editing.internal,
              }
            : null
        }
        codeField={{ label: 'Short code', placeholder: 'CFO' }}
        kindField={{
          label: 'Kind',
          trueLabel: 'Internal',
          falseLabel: 'Customer',
          hint: 'Internal roles are for back-office / admin work — not surfaced on customer-facing reports.',
        }}
        // The API field is `shortCode`; the sheet's form field is `code`.
        // Same translation for `internal` ↔ `kind`. Server field-error
        // paths land on the correct input via this map.
        serverToFormField={{ shortCode: 'code', internal: 'kind' }}
        submit={(body) => {
          // codeField is set → the sheet enforces presence, so `body.code`
          // is non-empty by the time we land here.
          if (!body.code) {
            throw new Error('code missing — CatalogFormSheet contract violation');
          }
          if (editing) {
            const patch: {
              name: string;
              shortCode: string;
              description: string | null;
              internal?: boolean;
            } = {
              name: body.name,
              shortCode: body.code,
              description: body.description,
            };
            // Only send `internal` when the user actually toggled Kind — a
            // no-op save shouldn't re-write the flag and stomp a concurrent
            // flip from another user/tab.
            if (body.kind !== undefined) patch.internal = body.kind;
            return serviceRolesApi.patch(editing.id, patch);
          }
          // CREATE: default to customer-facing if the user didn't touch
          // the picker (matches the DB default and the form's seeded
          // value).
          return serviceRolesApi.create({
            name: body.name,
            shortCode: body.code,
            description: body.description,
            internal: body.kind ?? false,
          });
        }}
        invalidateKeys={
          editing
            ? [
                queryKeys.serviceRoles.all,
                queryKeys.audit.all,
                queryKeys.serviceRoles.detail(editing.id),
              ]
            : [queryKeys.serviceRoles.all, queryKeys.audit.all]
        }
      />

      <ServiceRoleImportSheet open={importOpen} onOpenChange={setImportOpen} />
    </PageShell>
  );
}

const defaultCell: ColumnDef<ServiceRole>['cell'] = ({ getValue }) => String(getValue() ?? '');

function stripDefaults(s: ListSearch): ListSearch {
  const out: ListSearch = {};
  if (s.q && s.q.length > 0) out.q = s.q;
  if (s.status && s.status !== 'active') out.status = s.status;
  if (s.kind && s.kind !== DEFAULT_KIND) out.kind = s.kind;
  if (s.page && s.page > 0) out.page = s.page;
  if (s.sortField && s.sortField !== DEFAULT_SORT_FIELD) out.sortField = s.sortField;
  if (s.sortDirection && s.sortDirection !== 'asc') out.sortDirection = s.sortDirection;
  return out;
}
